import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { auth, db } from "../../firebase";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, collection, getDocs, query, where, limit } from "firebase/firestore";
import { httpsCallable, getFunctions } from "firebase/functions";
import SubscriptionModal from "../../components/SubscriptionModal";
import VideoThumbnail from "../../components/VideoThumbnail";
import { Icons } from "../../lib/Icons";

const SoloActivityDetail = () => {
  const { activityId } = useParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activity, setActivity] = useState(null);
  const [childActivities, setChildActivities] = useState([]);
  const [relatedActivities, setRelatedActivities] = useState([]);
  const [linkedArticle, setLinkedArticle] = useState(null);
  const [activeTab, setActiveTab] = useState('howto');
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [showPaywall, setShowPaywall] = useState(false);
  const [hasSubscription, setHasSubscription] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);

      // Load activity first (so we can show the title in the paywall)
      // Available to all users, authenticated or not
      await loadActivity();

      // Check subscription only if user is authenticated
      if (currentUser) {
        try {
          const functions = getFunctions(undefined, 'europe-west3');
          const checkSub = httpsCallable(functions, 'checkSoloSubscriptionFunc');
          const result = await checkSub();

          // School users get full access - no paywall
          if (result.data.status === 'not_solo_user') {
            setHasSubscription(true);
            setShowPaywall(false);
          } else if (!result.data.hasActiveSubscription) {
            // Show paywall for solo users without subscription
            setHasSubscription(false);
            setShowPaywall(true);
          } else {
            setHasSubscription(true);
          }
        } catch (e) {
          console.error('Subscription check failed:', e);
          // Show paywall on error (assume no subscription)
          setShowPaywall(true);
          setHasSubscription(false);
        }
      } else {
        // Unauthenticated users always see the paywall
        setHasSubscription(false);
        setShowPaywall(true);
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate, activityId]);

  const loadActivity = async () => {
    try {
      const docRef = doc(db, 'kb_items', activityId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const activityData = { id: docSnap.id, ...docSnap.data() };
        setActivity(activityData);

        // Load child activities for intervention umbrella (e.g. Attention Autism stages)
        if (activityData.childSlugs?.length > 0) {
          const children = [];
          for (const slug of activityData.childSlugs) {
            const childRef = doc(db, 'kb_items', slug);
            const childSnap = await getDoc(childRef);
            if (childSnap.exists()) {
              children.push({ id: childSnap.id, ...childSnap.data() });
            }
          }
          setChildActivities(children);
        } else {
          setChildActivities([]);
        }

        // Load related activities by domain (only for non-umbrella activities)
        if (activityData.targetDomains?.length > 0 && !activityData.childSlugs?.length) {
          await loadRelatedActivities(activityData.targetDomains, activityId);
        }
        
        // Load linked article if exists
        if (activityData.linkedArticle) {
          await loadLinkedArticle(activityData.linkedArticle);
        }
      } else {
        navigate("/solo/dashboard");
      }
    } catch (error) {
      console.error('Error loading activity:', error);
    }
  };

  const loadRelatedActivities = async (domains, currentId) => {
    try {
      const kbRef = collection(db, 'kb_items');
      const snapshot = await getDocs(kbRef);
      
      const related = [];
      snapshot.forEach(doc => {
        if (doc.id === currentId) return;
        const data = doc.data();
        const hasMatchingDomain = data.targetDomains?.some(d => domains.includes(d));
        if (hasMatchingDomain) {
          related.push({ id: doc.id, ...data });
        }
      });

      // Shuffle and take first 3
      const shuffled = related.sort(() => Math.random() - 0.5);
      setRelatedActivities(shuffled.slice(0, 3));
    } catch (error) {
      console.error('Error loading related activities:', error);
    }
  };

  const loadLinkedArticle = async (articleSlug) => {
    try {
      const articleRef = doc(db, 'sensi_articles', articleSlug);
      const articleSnap = await getDoc(articleRef);
      
      if (articleSnap.exists()) {
        setLinkedArticle({ id: articleSnap.id, ...articleSnap.data() });
      }
    } catch (error) {
      console.error('Error loading linked article:', error);
    }
  };

  const extractVideoId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    return match ? match[1] : null;
  };

  const normalizeResources = (activity) => {
    if (!activity || !activity.resources) {
      return [];
    }

    // New array format
    if (Array.isArray(activity.resources)) {
      return activity.resources;
    }

    // Legacy object format
    if (typeof activity.resources === 'object') {
      const resources = [];
      const legacy = activity.resources;
      const activityId = activity.slug || activity.id || 'activity';

      // Convert legacy videos
      const videos = legacy.video || legacy.videos || [];
      videos.forEach((video, i) => {
        resources.push({
          id: video.id || `legacy-video-${i}`,
          kind: 'video',
          title: video.title || video.name || 'Video',
          source: 'youtube',
          baseUrl: video.url || video.baseUrl || '',
          affiliate: video.affiliate || { enabled: false },
          redirectId: video.redirectId || `${activityId}-redirect-video-${i}`,
          status: video.status || { isActive: true },
          meta: video.meta || {},
        });
      });

      // Convert legacy buy items
      const buy = legacy.buy || [];
      buy.forEach((item, i) => {
        const isPdf = item.url && (item.url.endsWith('.pdf') || item.url.startsWith('/worksheets/'));
        resources.push({
          id: item.id || `legacy-buy-${i}`,
          kind: isPdf ? 'download' : 'buy',
          title: item.name || item.title || 'Resource',
          source: isPdf ? 'generated' : 'amazon',
          baseUrl: item.url || '',
          affiliate: item.affiliate || { enabled: false },
          redirectId: item.redirectId || `${activityId}-redirect-buy-${i}`,
          status: item.status || { isActive: true },
          meta: item.meta || {},
        });
      });

      // Convert legacy diy items
      const diy = legacy.diy || [];
      diy.forEach((item, i) => {
        resources.push({
          id: item.id || `legacy-diy-${i}`,
          kind: 'diy',
          title: item.name || item.title || 'DIY Guide',
          source: 'other',
          baseUrl: item.url || '',
          affiliate: { enabled: false },
          redirectId: item.redirectId || `${activityId}-redirect-diy-${i}`,
          status: item.status || { isActive: true },
          meta: item.meta || {},
        });
      });

      return resources;
    }

    return [];
  };

  const getVideoResources = () => {
    const normalized = normalizeResources(activity);
    return normalized.filter(r => r.kind === 'video' && r.status?.isActive !== false);
  };

  const getNonVideoResources = () => {
    const normalized = normalizeResources(activity);
    return normalized.filter(r => r.kind !== 'video');
  };

  /** PDF resources (download/doc with .pdf or /worksheets/ URL) that are active and available */
  const getPdfResources = () => {
    const normalized = normalizeResources(activity);
    return normalized.filter(
      (r) =>
        (r.kind === 'download' || r.kind === 'doc') &&
        r.baseUrl &&
        (r.baseUrl.endsWith('.pdf') || r.baseUrl.startsWith('/worksheets/')) &&
        r.status?.isActive !== false &&
        r.status?.lastOk !== false
    );
  };

  const videoResources = getVideoResources();
  const nonVideoResources = getNonVideoResources();
  const pdfResources = getPdfResources();
  const firstPdfResource = pdfResources[0];

  const getResourcePdfHref = (resource) => {
    if (!resource?.baseUrl) return null;
    const isStaticPdf =
      resource.baseUrl.startsWith('/worksheets/') || resource.baseUrl.endsWith('.pdf');
    if (isStaticPdf) {
      return resource.baseUrl.startsWith('http')
        ? resource.baseUrl
        : `${window.location.origin}${resource.baseUrl.startsWith('/') ? '' : '/'}${resource.baseUrl}`;
    }
    if (resource.redirectId) {
      const slug = activity?.slug || activity?.id;
      return `https://europe-west3-sen-ai-v2.cloudfunctions.net/resourceRedirect?redirectId=${resource.redirectId}&app=sen-ed&kbSlug=${slug}`;
    }
    return resource.baseUrl.startsWith('http')
      ? resource.baseUrl
      : `${window.location.origin}${resource.baseUrl.startsWith('/') ? '' : '/'}${resource.baseUrl}`;
  };
  const pdfHref = firstPdfResource ? getResourcePdfHref(firstPdfResource) : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500/20 border-t-purple-500 mx-auto mb-4"></div>
          <p className="text-slate-400">Loading activity...</p>
        </div>
      </div>
    );
  }

  if (!activity) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl text-white mb-4">Activity not found</h2>
          <Link to="/solo/dashboard" className="text-purple-400 hover:text-purple-300">
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  // Intervention umbrella (e.g. Attention Autism): show title, description, and list of stages
  if (activity.childSlugs?.length > 0) {
    return (
      <div className="min-h-screen bg-slate-900">
        <SubscriptionModal
          isOpen={showPaywall}
          onClose={() => navigate('/solo/dashboard')}
          activityTitle={activity.title || "this resource"}
          user={user}
        />
        <header className="sticky top-0 z-50 bg-slate-900/80 backdrop-blur-lg border-b border-slate-700/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link to="/solo/dashboard" className="flex items-center gap-3">
                <img src="/brand/logo-thumbnail-purple.png" alt="SEN-Ed" className="w-10 h-10 rounded-xl" />
                <span className="text-xl font-bold text-white">SEN-Ed</span>
              </Link>
              <button onClick={() => navigate(-1)} className="text-slate-400 hover:text-white transition-colors">
                ← Back
              </button>
            </div>
          </div>
        </header>
        <main className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 transition-all ${showPaywall ? 'blur-sm pointer-events-none select-none' : ''}`}>
          <div className="mb-8">
            <div className="flex flex-wrap gap-2 mb-4">
              {activity.targetDomains?.map(domain => (
                <span key={domain} className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full text-sm">
                  {domain}
                </span>
              ))}
              {activity.ageRange && (
                <span className="px-3 py-1 bg-slate-700 text-slate-300 rounded-full text-sm">
                  Ages {activity.ageRange.min}-{activity.ageRange.max}
                </span>
              )}
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">{activity.title}</h1>
            <p className="text-lg text-slate-300 max-w-3xl">{activity.description}</p>
            {activity.howTo?.length > 0 && (
              <div className="mt-6 p-4 bg-slate-800 rounded-xl">
                <h3 className="text-sm font-medium text-slate-400 mb-2">Overview</h3>
                <p className="text-slate-300">{activity.howTo[0]}</p>
              </div>
            )}
          </div>
          <section>
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <Icons.ClipboardDocumentList className="w-5 h-5 text-purple-400" />
              Stages
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {childActivities.map(child => (
                <Link
                  key={child.id}
                  to={`/solo/activity/${child.id}`}
                  className="block p-6 bg-slate-800 rounded-2xl hover:bg-slate-700 hover:ring-2 hover:ring-purple-500/50 transition-all"
                >
                  <h3 className="text-lg font-semibold text-white mb-2">{child.title}</h3>
                  <p className="text-slate-400 text-sm line-clamp-2">{child.description}</p>
                  <span className="inline-flex items-center gap-1 text-purple-400 text-sm mt-3">
                    View stage <Icons.ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              ))}
            </div>
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Subscription Paywall Modal */}
      <SubscriptionModal 
        isOpen={showPaywall} 
        onClose={() => navigate('/solo/dashboard')}
        activityTitle={activity?.title || "this resource"}
        user={user}
      />

      {/* Header */}
      <header className="sticky top-0 z-50 bg-slate-900/80 backdrop-blur-lg border-b border-slate-700/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/solo/dashboard" className="flex items-center gap-3">
              <img 
                src="/brand/logo-thumbnail-purple.png"
                alt="SEN-Ed" 
                className="w-10 h-10 rounded-xl"
              />
              <span className="text-xl font-bold text-white">SEN-Ed</span>
            </Link>

            <button 
              onClick={() => navigate(-1)}
              className="text-slate-400 hover:text-white transition-colors"
            >
              ← Back
            </button>
          </div>
        </div>
      </header>

      <main className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 transition-all ${showPaywall ? 'blur-sm pointer-events-none select-none' : ''}`}>
        {/* Hero Section */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-2 mb-4">
            {activity.targetDomains?.map(domain => (
              <span 
                key={domain}
                className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full text-sm"
              >
                {domain}
              </span>
            ))}
            {activity.ageRange && (
              <span className="px-3 py-1 bg-slate-700 text-slate-300 rounded-full text-sm">
                Ages {activity.ageRange.min}-{activity.ageRange.max}
              </span>
            )}
          </div>
          
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {activity.title}
          </h1>
          
          <p className="text-lg text-slate-300 max-w-3xl">
            {activity.description}
          </p>

          {/* Quick info */}
          {activity.recommendedFrequency && (
            <div className="mt-6 flex flex-wrap gap-4">
              <div className="flex items-center gap-2 px-4 py-2 bg-slate-800 rounded-xl">
                <Icons.Clock className="w-5 h-5 text-purple-400" />
                <span className="text-slate-300">
                  {activity.recommendedFrequency.sessionMinutes} min sessions
                </span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-slate-800 rounded-xl">
                <Icons.Calendar className="w-5 h-5 text-purple-400" />
                <span className="text-slate-300">
                  {activity.recommendedFrequency.perWeek}x per week
                </span>
              </div>
            </div>
          )}

          {/* Download PDF - only when a PDF resource is available */}
          {pdfHref && (
            <div className="mt-6">
              <a
                href={pdfHref}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-3 bg-purple-600 hover:bg-purple-500 text-white font-medium rounded-xl transition-colors"
                title="Open PDF in new tab"
              >
                <Icons.ArrowDownTray className="w-5 h-5" />
                Download PDF
              </a>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Video Section */}
            {videoResources.length > 0 && (
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                  <Icons.Film className="w-5 h-5 text-purple-400" />
                  Video Resources
                </h2>
                
                {/* Main video player */}
                {(selectedVideo || videoResources[0]) && (() => {
                  const video = selectedVideo || videoResources[0];
                  const videoId = extractVideoId(video.baseUrl);
                  return videoId ? (
                    <div className="mb-4">
                      <div className="aspect-video bg-slate-800 rounded-2xl overflow-hidden">
                        <iframe
                          src={`https://www.youtube.com/embed/${videoId}`}
                          title={video.title}
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                      <p className="mt-2 text-slate-300">{video.title}</p>
                    </div>
                  ) : null;
                })()}

                {/* Video thumbnails */}
                {videoResources.length > 1 && (
                  <div className="flex gap-3 overflow-x-auto pb-2">
                    {videoResources.map((video, idx) => {
                      const videoId = extractVideoId(video.baseUrl);
                      return (
                        <button
                          key={video.id || idx}
                          onClick={() => setSelectedVideo(video)}
                          className={`flex-shrink-0 w-32 rounded-xl overflow-hidden border-2 transition-all ${
                            (selectedVideo?.id || videoResources[0]?.id) === video.id
                              ? 'border-purple-500'
                              : 'border-transparent hover:border-slate-600'
                          }`}
                        >
                          <VideoThumbnail
                            src={video.meta?.thumbnail}
                            videoId={videoId}
                            alt={video.title}
                            className="w-full aspect-video object-cover"
                          />
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Tabs */}
            <div className="border-b border-slate-700 mb-6">
              <nav className="flex gap-6">
                {[
                  { id: 'howto', label: 'How To', Icon: Icons.ClipboardDocumentList },
                  { id: 'watchout', label: 'Watch Out For', Icon: Icons.ExclamationTriangle },
                  { id: 'resources', label: 'Resources', Icon: Icons.Link },
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 py-3 border-b-2 transition-colors ${
                      activeTab === tab.id
                        ? 'border-purple-500 text-purple-400'
                        : 'border-transparent text-slate-400 hover:text-slate-300'
                    }`}
                  >
                    <tab.Icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="bg-slate-800 rounded-2xl p-6">
              {activeTab === 'howto' && (
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4">How to Implement</h3>
                  {activity.howTo ? (
                    <ol className="space-y-4">
                      {activity.howTo.map((step, idx) => (
                        <li key={idx} className="flex gap-4">
                          <span className="flex-shrink-0 w-8 h-8 bg-purple-500/20 text-purple-400 rounded-full flex items-center justify-center text-sm font-medium">
                            {idx + 1}
                          </span>
                          <p className="text-slate-300 pt-1">{step}</p>
                        </li>
                      ))}
                    </ol>
                  ) : (
                    <p className="text-slate-400">No implementation steps available.</p>
                  )}
                </div>
              )}

              {activeTab === 'watchout' && (
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4">Things to Watch Out For</h3>
                  {activity.watchOutFor ? (
                    <ul className="space-y-3">
                      {activity.watchOutFor.map((item, idx) => (
                        <li key={idx} className="flex gap-3">
                          <Icons.ExclamationTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                          <p className="text-slate-300">{item}</p>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-slate-400">No watch-out items listed.</p>
                  )}
                </div>
              )}

              {activeTab === 'resources' && (
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4">Resources</h3>
                  {/* External buy/product links are hidden until we have a better way to curate them. */}

                  {/* DIY steps (no external links) */}
                  {activity.resources?.diy && activity.resources.diy.length > 0 && (
                    <div className="mb-6">
                      <h4 className="text-sm font-medium text-slate-400 mb-3 flex items-center gap-2">
                        <Icons.Cog6Tooth className="w-4 h-4" />
                        How to Run This Intervention
                      </h4>
                      <div className="space-y-4">
                        {activity.resources.diy.map((diy, idx) => (
                          <div key={idx} className="p-4 bg-slate-700/50 rounded-xl">
                            <h5 className="text-white font-medium mb-2">{diy.name || diy.title}</h5>
                            {diy.steps && (
                              <ol className="space-y-2">
                                {diy.steps.map((step, sIdx) => (
                                  <li key={sIdx} className="flex gap-2 text-slate-300 text-sm">
                                    <span className="text-purple-400">{sIdx + 1}.</span>
                                    {step}
                                  </li>
                                ))}
                              </ol>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {(videoResources.length > 0 || nonVideoResources.length > 0) ? (
                    <p className="text-slate-400 text-sm">Videos and downloads appear in the main Resources section below.</p>
                  ) : !activity.resources?.diy?.length && (
                    <p className="text-slate-400">No resources available for this activity.</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            {/* Tags */}
            {activity.tags && activity.tags.length > 0 && (
              <div className="bg-slate-800 rounded-2xl p-6 mb-6">
                <h3 className="text-lg font-semibold text-white mb-4">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {activity.tags.map((tag, idx) => (
                    <Link
                      key={idx}
                      to={`/solo/search?q=${encodeURIComponent(tag)}`}
                      className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-full text-sm transition-colors"
                    >
                      {tag}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Related Article */}
            {linkedArticle && (
              <div className="bg-slate-800 rounded-2xl p-6 mb-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Icons.BookOpen className="w-5 h-5 text-purple-400" />
                  Related Article
                </h3>
                <Link
                  to={`/solo/articles/${linkedArticle.slug}`}
                  className="block p-4 bg-gradient-to-r from-purple-500/20 to-indigo-500/20 rounded-xl hover:from-purple-500/30 hover:to-indigo-500/30 transition-colors border border-purple-500/30"
                >
                  <h4 className="text-white font-medium mb-2">
                    {linkedArticle.title}
                  </h4>
                  <p className="text-slate-400 text-sm line-clamp-2">
                    {linkedArticle.summary}
                  </p>
                  <span className="inline-flex items-center gap-1 text-purple-400 text-sm mt-3">
                    Read Article <Icons.ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              </div>
            )}

            {/* Related Activities */}
            {relatedActivities.length > 0 && (
              <div className="bg-slate-800 rounded-2xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Related Activities</h3>
                <div className="space-y-4">
                  {relatedActivities.map(related => (
                    <Link
                      key={related.id}
                      to={`/solo/activity/${related.id}`}
                      className="block p-4 bg-slate-700/50 rounded-xl hover:bg-slate-700 transition-colors"
                    >
                      <h4 className="text-white font-medium mb-1 line-clamp-1">
                        {related.title}
                      </h4>
                      <p className="text-slate-400 text-sm line-clamp-2">
                        {related.description}
                      </p>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default SoloActivityDetail;


